if (!customElements.get('infographic-photoswipe')) {
	/**
	 * InfographicPhotoSwipe - Custom element for image zoom functionality using PhotoSwipe
	 * @class InfographicPhotoSwipe
	 * @extends HTMLElement
	 */
	class InfographicPhotoSwipe extends HTMLElement {
		constructor() {
			super();

			this.checkInterval = null;
			this.zoomLinks = [];
			this.items = [];
			this.pswpElement = null;
			this.isMultipleImages = false;

			// Bind handlers for proper cleanup
			this.handleLinkClick = this.handleLinkClick.bind(this);
			this.handleLinkKeydown = this.handleLinkKeydown.bind(this);
		}

		connectedCallback() {
			if (document.readyState === 'loading') {
				document.addEventListener('DOMContentLoaded', () => this.init());
			} else {
				this.init();
			}
		}

		disconnectedCallback() {
			this.cleanup();
		}

		cleanup() {
			// Clear polling interval if still running
			if (this.checkInterval) {
				clearInterval(this.checkInterval);
				this.checkInterval = null;
			}

			// Remove event listeners from zoom links
			this.zoomLinks.forEach((link) => {
				link.removeEventListener('click', this.handleLinkClick);
				link.removeEventListener('keydown', this.handleLinkKeydown);
			});

			this.zoomLinks = [];
			this.items = [];
		}

		init() {
			requestAnimationFrame(() => {
				this.waitForPhotoSwipe(() => {
					this.setupPhotoSwipe();
				});
			});
		}

		waitForPhotoSwipe(callback) {
			if (typeof PhotoSwipe !== 'undefined') {
				callback();
				return;
			}

			let attempts = 0;
			const maxAttempts = 50;

			this.checkInterval = setInterval(() => {
				attempts++;
				if (typeof PhotoSwipe !== 'undefined') {
					clearInterval(this.checkInterval);
					this.checkInterval = null;
					callback();
				} else if (attempts >= maxAttempts) {
					clearInterval(this.checkInterval);
					this.checkInterval = null;
					console.warn('PhotoSwipe library failed to load after timeout');
				}
			}, 100);
		}

		setupPhotoSwipe() {
			this.zoomLinks = Array.from(this.querySelectorAll('[data-zoom-image]'));

			if (!this.zoomLinks.length) {
				return;
			}

			const images = Array.from(this.querySelectorAll('.infographic--photoswipe'));
			this.items = this.buildItems(images);

			if (this.items.length === 0) {
				return;
			}

			this.isMultipleImages = this.items.length > 1;
			this.pswpElement = document.querySelector('.pswp');

			if (!this.pswpElement) {
				console.warn('PhotoSwipe element (.pswp) not found in DOM');
				return;
			}

			this.zoomLinks.forEach((link) => {
				link.addEventListener('click', this.handleLinkClick);
				link.addEventListener('keydown', this.handleLinkKeydown);
			});
		}

		handleLinkClick(event) {
			event.preventDefault();
			event.stopPropagation();

			const link = event.currentTarget;
			this.openPhotoSwipe(link);
		}

		handleLinkKeydown(event) {
			if (event.key === 'Enter' || event.key === ' ') {
				event.preventDefault();
				event.currentTarget.click();
			}
		}

		openPhotoSwipe(link) {
			if (typeof PhotoSwipe === 'undefined') {
				console.warn('PhotoSwipe library not loaded');
				return;
			}

			const itemIndex = this.findItemIndex(link);

			if (itemIndex === -1) {
				return;
			}

			const options = this.buildOptions(itemIndex, link);
			const pswp = new PhotoSwipe(this.pswpElement, PhotoSwipeUI_Default, this.items, options);

			this.setupPhotoSwipeListeners(pswp);
			pswp.init();
		}

		findItemIndex(link) {
			const linkSrc = this.getCleanSrc(link.dataset.imageSrc || link.getAttribute('href'));

			const itemIndex = this.items.findIndex(item => {
				const itemSrc = this.getCleanSrc(item.src);
				return itemSrc === linkSrc;
			});

			if (itemIndex === -1) {
				console.warn('Could not find item for clicked image');
			}

			return itemIndex;
		}

		getCleanSrc(src) {
			if (!src) return '';
			return src.split('?')[0];
		}

		buildOptions(itemIndex, link) {
			const mainClass = this.isMultipleImages ? 'pswp-zoom-gallery' : 'pswp-zoom-gallery pswp-zoom-gallery--single';

			return {
				index: itemIndex,
				mainClass: mainClass,
				maxSpreadZoom: 4,
				loop: this.isMultipleImages,
				allowPanToNext: this.isMultipleImages,
				closeOnScroll: false,
				closeOnVerticalDrag: false,
				showHideOpacity: true,
				arrowKeys: this.isMultipleImages,
				history: false,
				focus: false,
				captionEl: false,
				fullscreenEl: false,
				zoomEl: true,
				shareEl: false,
				counterEl: this.isMultipleImages,
				arrowEl: this.isMultipleImages,
				preloaderEl: true,
				tapToClose: false,
				clickToCloseNonZoomable: false,
				pinchToClose: false,
				spacing: 0,
				getDoubleTapZoom: this.getDoubleTapZoom,
				getThumbBoundsFn: () => this.getThumbBounds(link)
			};
		}

		getDoubleTapZoom(isMouseClick, item) {
			if (!item || !item.w || !item.h) return 1;

			const viewportWidth = window.innerWidth;
			const viewportHeight = window.innerHeight;
			const fitRatio = Math.min(viewportWidth / item.w, viewportHeight / item.h);

			// If image fits within viewport (fitRatio >= 1), zoom to actual size
			// If image is larger than viewport, zoom to show more detail
			if (fitRatio >= 1) {
				// Image is smaller than viewport, zoom to 2x
				return 2;
			} else {
				// Image is larger than viewport
				// On double-tap/click, zoom to show image at actual size (1:1) or 1.5x fit ratio
				const targetZoom = Math.max(1, fitRatio * 2);
				return Math.min(targetZoom, 4); // Cap at 4x
			}
		}

		getThumbBounds(link) {
			const thumbnail = link.closest('.infographic--photoswipe');
			const pageYScroll = window.pageYOffset || document.documentElement.scrollTop;
			const rect = thumbnail ? thumbnail.getBoundingClientRect() : { left: 0, top: 0, width: 0 };

			return {
				x: rect.left,
				y: rect.top + pageYScroll,
				w: rect.width
			};
		}

		setupPhotoSwipeListeners(pswp) {
			pswp.listen('firstUpdate', function () {
				pswp.listen('parseVerticalMargin', function (item) {
					item.vGap = { top: 10, bottom: 10 };
				});
			});
		}

		buildItems(images) {
			const items = [];

			images.forEach((imageWrapper) => {
				const item = this.buildItem(imageWrapper);
				if (item) {
					items.push(item);
				}
			});

			return items;
		}

		buildItem(imageWrapper) {
			const link = imageWrapper.querySelector('[data-zoom-image]');

			if (!link) {
				return null;
			}

			const src = link.dataset.imageSrc || link.getAttribute('href');
			const width = this.parseImageDimension(link.dataset.imageWidth);
			const height = this.parseImageDimension(link.dataset.imageHeight);
			const msrc = link.dataset.msrc || src;

			if (!src || !width || !height) {
				console.warn('Invalid image data for PhotoSwipe item', { src, width, height });
				return null;
			}

			return {
				src: src,
				w: width,
				h: height,
				msrc: msrc
			};
		}

		parseImageDimension(value) {
			if (!value) return 0;

			const parsed = parseInt(value, 10);

			if (isNaN(parsed) || parsed <= 0) {
				return 0;
			}

			return parsed;
		}
	}

	customElements.define('infographic-photoswipe', InfographicPhotoSwipe);
}
